import React from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { motion } from "framer-motion";

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

// Investment options content
const investmentOptions = [
  {
    title: "Stocks (Shares)",
    content:
      "Buying stocks means owning a portion of a company. Your returns come from stock price increases and dividends (if paid).",
    risk: "Medium to High",
    riskLevel: "medium-high",
    howToInvest: [
      "Buy individual stocks through a stock brokerage.",
      "Invest in exchange-traded funds (ETFs) that track a group of stocks.",
      "Consider managed funds if you prefer professional management.",
    ],
  },
  {
    title: "Bonds",
    content:
      "Bonds are loans you give to companies or governments in exchange for fixed interest payments over time.",
    risk: "Low to Medium",
    riskLevel: "low-medium",
    howToInvest: [
      "Purchase government or corporate bonds through a broker.",
      "Invest in bond ETFs for diversification.",
    ],
  },
  {
    title: "Real Estate",
    content:
      "Investing in property allows you to earn income through rental returns and property appreciation.",
    risk: "Medium",
    riskLevel: "medium",
    howToInvest: [
      "Buy residential or commercial property for rental income.",
      "Invest in Real Estate Investment Trusts (REITs) for exposure without directly owning property.",
    ],
  },
  {
    title: "Mutual Funds & ETFs",
    content:
      "A mutual fund pools money from investors to buy a variety of stocks, bonds, or other assets. ETFs work similarly but trade like stocks on an exchange.",
    risk: "Low to High",
    riskLevel: "low-high",
    howToInvest: [
      "Choose a fund that matches your risk tolerance and goals.",
      "Invest through financial institutions or online investment platforms.",
    ],
  },
  {
    title: "Cryptocurrency",
    content:
      "Digital currencies like Bitcoin and Ethereum operate on blockchain technology and can be traded for profit.",
    risk: "High",
    riskLevel: "high",
    howToInvest: [
      "Buy cryptocurrency through a crypto exchange.",
      "Store your digital assets in a secure wallet.",
    ],
  },
  {
    title: "Commodities",
    content:
      "Investing in physical goods like gold, silver, or oil can be a hedge against inflation.",
    risk: "Medium to High",
    riskLevel: "medium-high",
    howToInvest: [
      "Buy physical commodities (gold, silver bars, etc.).",
      "Trade commodity futures or ETFs.",
    ],
  },
  {
    title: "Peer-to-Peer Lending",
    content:
      "This involves lending money to individuals or businesses through online platforms and earning interest in return.",
    risk: "Medium",
    riskLevel: "medium",
    howToInvest: [
      "Use P2P lending platforms to invest in loans with varying risk levels.",
    ],
  },
  {
    title: "Savings & Term Deposits",
    content:
      "These are low-risk options that earn interest on your savings with guaranteed returns.",
    risk: "Low",
    riskLevel: "low",
    howToInvest: [
      "Open a high-interest savings account.",
      "Lock in funds for a fixed period in a term deposit for higher interest rates.",
    ],
  },
];

const HowToInvest = () => {
  const professionalStyles = {
    heroSection: {
      background: "linear-gradient(135deg, #001f3f 0%, #003366 100%)",
      padding: "100px 50px",
      marginBottom: "80px",
    },
    heroTitle: {
      fontSize: "3.5rem",
      fontWeight: "700",
      letterSpacing: "-0.5px",
      marginBottom: "25px",
      color: "#fff",
      textTransform: "uppercase",
      textAlign: "center",
    },
    heroText: {
      fontSize: "1.2rem",
      maxWidth: "900px",
      margin: "0 auto",
      lineHeight: "1.8",
      fontWeight: "300",
      color: "#fff",
      textAlign: "center",
    },
    investmentCard: {
      border: "none",
      borderLeft: "4px solid #003366",
      background: "#fff",
      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
      borderRadius: "0",
      padding: "35px",
      height: "100%",
      transition: "all 0.3s ease",
    },
    cardNumber: {
      display: "inline-block",
      width: "45px",
      height: "45px",
      background: "#003366",
      color: "#fff",
      fontSize: "1.3rem",
      fontWeight: "700",
      textAlign: "center",
      lineHeight: "45px",
      marginBottom: "15px",
    },
    cardTitle: {
      fontSize: "1.5rem",
      fontWeight: "700",
      color: "#003366",
      marginBottom: "20px",
      textTransform: "uppercase",
      letterSpacing: "-0.5px",
    },
    cardDescription: {
      fontSize: "1rem",
      lineHeight: "1.8",
      color: "#555",
      marginBottom: "20px",
    },
    riskSection: {
      padding: "15px 0",
      marginBottom: "20px",
      borderTop: "1px solid #e0e0e0",
      borderBottom: "1px solid #e0e0e0",
    },
    riskLabel: {
      fontSize: "0.85rem",
      fontWeight: "600",
      color: "#003366",
      textTransform: "uppercase",
      letterSpacing: "1px",
      marginBottom: "5px",
    },
    riskValue: {
      fontSize: "1rem",
      fontWeight: "500",
      color: "#555",
    },
    sectionTitle: {
      fontSize: "1.1rem",
      fontWeight: "700",
      color: "#003366",
      marginBottom: "15px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    investmentList: {
      listStyle: "none",
      padding: "0",
      margin: "0",
    },
    investmentListItem: {
      position: "relative",
      paddingLeft: "25px",
      marginBottom: "12px",
      fontSize: "0.95rem",
      lineHeight: "1.7",
      color: "#555",
    },
  };

  return (
    <Container fluid style={{ padding: 0 }}>
      {/* Hero Section */}
      <motion.div
        style={professionalStyles.heroSection}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <motion.h1
          style={professionalStyles.heroTitle}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          How to Invest
        </motion.h1>
        <motion.p
          style={professionalStyles.heroText}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          Investing is a powerful way to grow your wealth and secure your
          financial future. Explore our comprehensive guide to various
          investment methods and find the strategy that aligns with your goals
          and risk tolerance.
        </motion.p>
      </motion.div>

      {/* Investment Options Grid */}
      <Container style={{ paddingBottom: "80px" }}>
        <Row className="g-4">
          {investmentOptions.map((investment, index) => (
            <Col md={6} lg={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                style={{ height: "100%" }}
              >
                <motion.div
                  whileHover={{
                    y: -8,
                    boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                  }}
                  style={{ height: "100%" }}
                >
                  <Card style={professionalStyles.investmentCard}>
                    <div style={professionalStyles.cardNumber}>{index + 1}</div>
                    <h3 style={professionalStyles.cardTitle}>
                      {investment.title}
                    </h3>
                    <p style={professionalStyles.cardDescription}>
                      {investment.content}
                    </p>
                    <div style={professionalStyles.riskSection}>
                      <div style={professionalStyles.riskLabel}>Risk Level</div>
                      <div style={professionalStyles.riskValue}>
                        {investment.risk}
                      </div>
                    </div>
                    <h4 style={professionalStyles.sectionTitle}>
                      How to Get Started
                    </h4>
                    <ul style={professionalStyles.investmentList}>
                      {investment.howToInvest.map((method, idx) => (
                        <li
                          key={idx}
                          style={professionalStyles.investmentListItem}
                        >
                          <span
                            style={{
                              position: "absolute",
                              left: "0",
                              top: "5px",
                              width: "6px",
                              height: "6px",
                              background: "#003366",
                            }}
                          />
                          {method}
                        </li>
                      ))}
                    </ul>
                  </Card>
                </motion.div>
              </motion.div>
            </Col>
          ))}
        </Row>

        {/* Call to Action Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{
            background: "#f8f9fa",
            padding: "60px 40px",
            marginTop: "80px",
            borderLeft: "4px solid #003366",
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: "700",
              color: "#003366",
              marginBottom: "20px",
              textTransform: "uppercase",
              letterSpacing: "-0.5px",
            }}
          >
            Need Investment Guidance?
          </h2>
          <p
            style={{
              fontSize: "1.1rem",
              lineHeight: "1.8",
              color: "#555",
              marginBottom: "30px",
              maxWidth: "700px",
              margin: "0 auto 30px",
            }}
          >
            Our expert advisors are here to help you navigate the investment
            landscape and develop a strategy tailored to your unique financial
            situation and goals.
          </p>
          <a
            href="/contact"
            style={{
              display: "inline-block",
              background: "#003366",
              color: "#fff",
              padding: "15px 45px",
              fontSize: "1.1rem",
              fontWeight: "600",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              textDecoration: "none",
              border: "2px solid #003366",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#fff";
              e.target.style.color = "#003366";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#003366";
              e.target.style.color = "#fff";
            }}
          >
            Schedule a Consultation
          </a>
        </motion.div>
      </Container>
    </Container>
  );
};

export default HowToInvest;
